import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Upload, ImageIcon, FileText, Brain, UserCheck, AlertTriangle, Database, ShieldCheck, Users, Zap, CheckCircle } from 'lucide-react';
import { Navbar, Footer } from './shared';
import { AlphaBanner } from './landing/AlphaBanner';
import { AlphaWaitlist } from './landing/AlphaWaitlist';

const LandingPage = ({ onLogin, onGuest, openRequestModal }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});
  
  const steps = [
    { icon: Upload, title: "Upload Scans", desc: "Upload scanned reports or images of tables", gradient: "from-blue-500 to-cyan-600", step: 1 },
    { icon: ImageIcon, title: "Pre-processing", desc: "Images cleaned and enhanced for readability", gradient: "from-purple-500 to-violet-600", step: 2 },
    { icon: FileText, title: "OCR Extraction", desc: "Text and tables are extracted using OCR", gradient: "from-teal-500 to-cyan-600", step: 3 },
    { icon: Brain, title: "AI Review", desc: "AI identifies and addresses data anomalies", gradient: "from-green-500 to-emerald-600", step: 4 },
    { icon: UserCheck, title: "Manual Review", desc: "Specialist team verifies and corrects complex cases", gradient: "from-yellow-500 to-amber-600", step: 5 },
    { icon: AlertTriangle, title: "Quality Assurance", desc: "Data scientists perform algorithmic data validation", gradient: "from-orange-500 to-red-500", step: 6 },
    { icon: Database, title: "Data Delivery", desc: "Final delivery in your preferred format", gradient: "from-red-500 to-pink-600", step: 7 }
  ];



  // Page load animation
  useEffect(() => {
    const timer = setTimeout(() => setIsPageLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-scroll effect with rewind - slower when hovered
  useEffect(() => {
    const intervalTime = isHovered ? 6000 : 3000; // 6s when hovered, 3s normal
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev === steps.length - 1) {
          // At step 7, rewind to step 1
          return 0; // Direct rewind without setTimeout to avoid conflicts
        }
        return prev + 1;
      });
    }, intervalTime);
    
    return () => clearInterval(interval);
  }, [steps.length, isHovered]); // Update interval when hover state changes

  const handleGuestClick = () => {
    navigate('/workspace');
  };

  const scrollToWorkflow = () => {
    document.getElementById('workflow')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`min-h-screen bg-white transition-opacity duration-1000 ease-out ${
      isPageLoaded ? 'opacity-100' : 'opacity-0'
    }`}>
      <Navbar openRequestModal={openRequestModal} />
      
      {/* Alpha Banner */}
      <AlphaBanner />

      {/* Hero Section */}
      <section 
        id="hero" 
        ref={(el) => (sectionRefs.current.hero = el)}
        className="px-6 py-16 bg-gradient-to-br from-white via-blue-50 to-sky-200"
      >
        <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${
          visibleSections.has('hero') || isPageLoaded 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}>
          <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight transition-all duration-1200 ease-out delay-200 ${
            visibleSections.has('hero') || isPageLoaded 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4'
          }`}>
            Transform unstructured tables into
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              clean & ready-to-use data
            </span>
          </h1>
          
          <p className={`text-lg md:text-xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto transition-all duration-1200 ease-out delay-400 ${
            visibleSections.has('hero') || isPageLoaded 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4'
          }`}>
            Advanced OCR meets expert validation to convert complex tables and documents into research-grade datasets
          </p>
          
          <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-1200 ease-out delay-500 ${
            visibleSections.has('hero') || isPageLoaded 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4'
          }`}>
            <button 
              onClick={openRequestModal}
              className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request a quote
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
            <button 
              onClick={() => navigate('/solution')}
              className="group inline-flex items-center px-8 py-4 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-xl font-semibold text-slate-800 hover:text-slate-900 hover:shadow-lg transition-all duration-300"
            >
              See our solutions
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Alpha Waitlist Section */}
      <AlphaWaitlist />

      {/* How It Works - Workflow Animation */}
      <section 
        id="workflow" 
        ref={(el) => (sectionRefs.current.workflow = el)}
        className="px-6 py-20 bg-gray-50"
      >
        <div className="max-w-5xl mx-auto">
          <div className={`text-center mb-8 transition-all duration-1000 ease-out ${
            visibleSections.has('workflow') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              How DigiTables works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transform your scanned tables into structured data with precision and speed at scale
            </p>
          </div>
          
          {/* Carousel Container */}
          <div className="relative">
            {/* Desktop & Tablet - Rotating Carousel */}
            <div 
              className="hidden sm:block relative h-96"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                {steps.map((item, index) => {
                  const relativePosition = index - currentIndex;
                  let translateX = relativePosition * 260;
                  let scale = 1;
                  let opacity = 1;
                  let zIndex = 10;
                  
                  if (relativePosition === 0) {
                    scale = 1.1;
                    opacity = 1;
                    zIndex = 20;
                  } else if (Math.abs(relativePosition) === 1) {
                    scale = 0.85;
                    opacity = 0.8;
                    zIndex = 15;
                  } else if (Math.abs(relativePosition) === 2) {
                    scale = 0.7;
                    opacity = 0.6;
                    zIndex = 12;
                  } else {
                    scale = 0.55;
                    opacity = 0.4;
                    zIndex = 8;
                  }
                  
                  const isFocused = relativePosition === 0;
                  
                  return (
                    <div 
                      key={index}
                      onClick={() => {
                        setCurrentIndex(index);
                        // Don't force resume on click - let hover state control pausing
                      }}
                      className="absolute cursor-pointer transition-all duration-500 ease-out"
                      style={{
                        transform: `translateX(${translateX}px) scale(${scale})`,
                        opacity: opacity,
                        zIndex: zIndex
                      }}
                    >
                      <div className={`group w-60 h-60 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-white/60 p-5 flex flex-col items-center justify-center text-center ${
                        isFocused ? 'ring-4 ring-blue-200 ring-opacity-50 shadow-xl' : ''
                      }`}>
                        <div className="flex items-center justify-center mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            isFocused ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            Step {item.step}
                          </span>
                        </div>
                        
                        <div className={`bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-200 shadow-md w-14 h-14`}>
                          <item.icon className="text-white drop-shadow-sm" size={22} />
                        </div>
                        
                        <div className="space-y-2 flex-1 flex flex-col justify-center">
                          <h3 className={`font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-200 leading-tight ${
                            isFocused ? 'text-xl' : 'text-lg'
                          }`}>
                            {item.title}
                          </h3>
                          <div className={`overflow-hidden transition-all duration-500 ease-out ${
                            isFocused ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
                          }`}>
                            <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-200 text-sm px-2">
                              {item.desc}
                            </p>
                          </div>
                        </div>
                        
                        <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-200`}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <button 
                onClick={() => {
                  setCurrentIndex((prev) => (prev - 1 + steps.length) % steps.length);
                  // Don't force resume on navigation - let hover state control pausing
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 z-30"
              >
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button 
                onClick={() => {
                  setCurrentIndex((prev) => (prev + 1) % steps.length);
                  // Don't force resume on navigation - let hover state control pausing
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 z-30"
              >
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      // Don't force resume on dot click - let hover state control pausing
                    }}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentIndex ? 'bg-blue-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

            </div>

            {/* Mobile - Simple Version */}
            <div 
              className="sm:hidden"
              onTouchStart={() => setIsHovered(true)}
              onTouchEnd={() => setIsHovered(false)}
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-5 max-w-sm mx-auto min-h-60 flex flex-col items-center justify-center text-center">
                <div className="flex items-center justify-center mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    Step {steps[currentIndex].step}
                  </span>
                </div>
                
                <div className={`bg-gradient-to-br ${steps[currentIndex].gradient} rounded-xl flex items-center justify-center mb-4 w-14 h-14 shadow-md`}>
                  {React.createElement(steps[currentIndex].icon, { className: "text-white drop-shadow-sm", size: 22 })}
                </div>
                
                <div className="space-y-2 flex-1 flex flex-col justify-center">
                  <h3 className="text-xl font-bold text-gray-900">{steps[currentIndex].title}</h3>
                  <div className="transition-opacity duration-500">
                    <p className="text-gray-600 text-sm leading-relaxed px-2">{steps[currentIndex].desc}</p>
                  </div>
                </div>
                
                <div className="flex gap-2 justify-center mt-6">
                  {steps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentIndex(index);
                        // Don't force resume on mobile dot click - let touch events control pausing
                      }}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentIndex ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Screenshot Section */}
      <section className="px-6 py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-100 mb-4">
              See DigiTables in action
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Transform complex scanned tables into clean, structured data with our intuitive platform
            </p>
          </div>
          
          <img 
            src="/demo-screenshot.png" 
            alt="DigiTables.ai demo showing PDF to structured data transformation"
            className="w-full h-auto"
          />
          
          <div className="text-center mt-12">
            <button 
              onClick={openRequestModal}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request a demo
              <ArrowRight className="ml-2" size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        id="stats" 
        ref={(el) => (sectionRefs.current.stats = el)}
        className="px-6 py-20 bg-gradient-to-br from-blue-50 via-indigo-50/50 to-purple-50/30"
      >
        <div className="max-w-4xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-1000 ease-out ${
            visibleSections.has('stats') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by researchers worldwide
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our rigorous methodology delivers research-grade precision and scalability<br />
              (Tier 3 Full Service)
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className={`text-center group transition-all duration-1000 ease-out delay-200 ${
              visibleSections.has('stats') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/60 group-hover:scale-105 h-48 flex flex-col justify-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                  100K+
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Pages processed</h3>
                <p className="text-gray-600 text-sm">Proven scale and reliability across diverse document types</p>
              </div>
            </div>
            
            <div className={`text-center group transition-all duration-1000 ease-out delay-400 ${
              visibleSections.has('stats') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/60 group-hover:scale-105 h-48 flex flex-col justify-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-4">
                  99.9%
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Typical accuracy rate</h3>
                <p className="text-gray-600 text-sm">Research-grade precision with human-in-the-loop validation</p>
              </div>
            </div>
            
            <div className={`text-center group transition-all duration-1000 ease-out delay-600 ${
              visibleSections.has('stats') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/60 group-hover:scale-105 h-48 flex flex-col justify-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  48hr
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Proposal response</h3>
                <p className="text-gray-600 text-sm">Detailed project proposal delivered within 48 hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        id="benefits" 
        ref={(el) => (sectionRefs.current.benefits = el)}
        className="px-6 py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('benefits') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-100 mb-4">
              Why choose DigiTables
            </h2>
            <p className="text-lg text-gray-300 mb-16 max-w-2xl mx-auto">
              Research-grade typical accuracy through proven workflows and rigorous quality controls
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className={`text-center transition-all duration-1000 ease-out delay-200 ${
              visibleSections.has('benefits') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Optimized process</h3>
              <p className="text-gray-300 text-sm max-w-xs mx-auto">
                Multiple vendors managed through our interface to accelerate timelines while maintaining quality
              </p>
            </div>

            <div className={`text-center transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('benefits') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Expert management</h3>
              <p className="text-gray-300 text-sm max-w-xs mx-auto">
                Daily check-ins and performance tracking ensure consistent progress and high-quality output
              </p>
            </div>

            <div className={`text-center transition-all duration-1000 ease-out delay-400 ${
              visibleSections.has('benefits') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Quality validation</h3>
              <p className="text-gray-300 text-sm max-w-xs mx-auto">
                Algorithmic and manual validation tools ensure consistency and research-grade typical accuracy
              </p>
            </div>

            <div className={`text-center transition-all duration-1000 ease-out delay-500 ${
              visibleSections.has('benefits') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Speed & typical accuracy</h3>
              <p className="text-gray-300 text-sm max-w-xs mx-auto">
                Efficient processes combined with rigorous error checks deliver reliable results at scale
              </p>
            </div>

            <div className={`text-center transition-all duration-1000 ease-out delay-600 ${
              visibleSections.has('benefits') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Database className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Ready-to-use data</h3>
              <p className="text-gray-300 text-sm max-w-xs mx-auto">
                Cleaned, validated datasets delivered in your preferred format—ready for immediate analysis
              </p>
            </div>

            <div className={`text-center transition-all duration-1000 ease-out delay-700 ${
              visibleSections.has('benefits') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Brain className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">AI + human QA</h3>
              <p className="text-gray-300 text-sm max-w-xs mx-auto">
                Advanced algorithms identify issues while human experts verify complex cases for maximum typical accuracy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="px-6 py-20 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to transform your data?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Get started with DigiTables today and experience research-grade data digitization.
          </p>
          
          <div className="flex justify-center">
            <button 
              onClick={openRequestModal}
              className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request a quote
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LandingPage;